import * as Sentry from '@sentry/react'

export type SentryEnvironment = 'local' | 'ephemeral' | 'staging' | 'production'

export const normalizeEnvironment = (mode: string): SentryEnvironment => {
  if (mode.startsWith('ephemeral')) {
    return 'ephemeral'
  }
  if (mode === 'production') {
    return 'production'
  }
  if (mode === 'staging') {
    return 'staging'
  }

  return 'local'
}

export const shouldInitSentry = (
  environment: SentryEnvironment,
  dsn: string | undefined,
): boolean => environment !== 'local' && Boolean(dsn)

export const initSentry = (): void => {
  const environment = normalizeEnvironment(import.meta.env.MODE)
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (environment === 'local') {
    return
  }

  if (!shouldInitSentry(environment, dsn)) {
    console.error(`No VITE_SENTRY_DSN for environment "${environment}" — error reporting is disabled`)

    return
  }

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const error = hint.originalException
      if (error instanceof Error) {
        if (error.message.includes('canceled') || error.message.includes('aborted')) {
          return null
        }
      }

      return event
    },
    ignoreErrors: [
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'login_required',
    ],
  })
}

/**
 * Capture an exception and send it to Sentry
 */
export const captureException = (
  error: Error,
  context?: Sentry.CaptureContext
): string => {
  return Sentry.captureException(error, context)
}

/**
 * Capture a message and send it to Sentry
 */
export const captureMessage = (
  message: string,
  level?: Sentry.SeverityLevel
): string => {
  return Sentry.captureMessage(message, level)
}

/**
 * Set user context for Sentry
 */
export const setUser = (user: Sentry.User | null): void => {
  Sentry.setUser(user)
}

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * Set custom context
 */
export const setContext = (name: string, context: Record<string, unknown>): void => {
  Sentry.setContext(name, context)
}

/**
 * Set a tag for filtering in Sentry
 */
export const setTag = (key: string, value: string): void => {
  Sentry.setTag(key, value)
}
